/**
 * joystick.ts — Turning a drag gesture into a movement vector.
 *
 * The on-screen stick is **floating**: it has no fixed home. Wherever you press
 * becomes the origin, and how far you drag from there decides both the direction
 * and the *speed*. That's the whole point of a stick over a d-pad — a small
 * nudge should amble, a full push should stride.
 *
 * Two knobs shape the feel:
 *   - `deadZone`  — slop before anything moves, so resting a thumb doesn't drift.
 *   - `maxRadius` — drag distance that means full speed; further is clamped.
 *
 * Magnitude ramps from 0 *at the edge of the dead zone* rather than jumping
 * straight to a fraction, so there's no lurch as you cross it.
 *
 * Screen and gameplay axes line up here: screen +x is world +x, and screen +y
 * (downward) is gameplay +y, which the renderer maps to world +z. So dragging
 * down walks "into" the screen, which is what a player expects.
 */

export interface JoystickReading {
  /** Unit direction, or (0,0) inside the dead zone. */
  x: number;
  y: number;
  /** 0 at the dead-zone edge → 1 at `maxRadius` and beyond. */
  magnitude: number;
}

const STILL: JoystickReading = { x: 0, y: 0, magnitude: 0 };

/**
 * Read the stick as a direction plus a 0..1 magnitude.
 *
 * @param dx  horizontal drag from the origin, in pixels
 * @param dy  vertical drag from the origin, in pixels (screen-down positive)
 */
export function readJoystick(
  dx: number,
  dy: number,
  maxRadius: number,
  deadZone: number,
): JoystickReading {
  const dist = Math.hypot(dx, dy);
  if (dist <= deadZone || dist === 0) return { ...STILL };

  // Guard a degenerate config (radius inside the dead zone) rather than dividing
  // by zero or negative and producing a wild magnitude.
  const usable = Math.max(maxRadius - deadZone, 1e-6);
  const magnitude = Math.min((dist - deadZone) / usable, 1);

  return { x: dx / dist, y: dy / dist, magnitude };
}

/**
 * Where to draw the knob relative to the stick's origin: the raw drag, clamped
 * to `maxRadius` so it never escapes the ring. Purely cosmetic — movement uses
 * `readJoystick`.
 */
export function knobOffset(
  dx: number,
  dy: number,
  maxRadius: number,
): { x: number; y: number } {
  const dist = Math.hypot(dx, dy);
  if (dist <= maxRadius || dist === 0) return { x: dx, y: dy };
  const scale = maxRadius / dist;
  return { x: dx * scale, y: dy * scale };
}

/**
 * Whether a press at this point should start the stick.
 *
 * The top strip of the screen is excluded so taps on the HUD readouts don't set
 * the player walking. Everything below it is fair game — the stick is meant to
 * appear under your thumb wherever that happens to be, and a large dead region
 * reads as the controls being broken rather than as a boundary.
 */
export function isInJoystickZone(
  y: number,
  viewportHeight: number,
  topFraction: number,
): boolean {
  return y >= viewportHeight * topFraction;
}
